import React, { useState, useEffect,useRef } from "react";
import Head from "next/head";
import Image from "next/image";
import IMAGES from "@/public/assets/img";
import AOS from "aos";
import "aos/dist/aos.css";
import Link from "next/link";
import { useRouter } from "next/router";
import enIN from '@/locales/partner-program/enIN';
import esMX from '@/locales/partner-program/enMX';
import enUS from "@/locales/partner-program/enUS";
import PartnerProgramForm from "@/component/shared/partner-program-Form/partnerProgramForm";
import PartnerProgramFormES from "@/component/shared/partner-program-Form/partner-program-FormES";
import { getMetadata } from "@/component/shared/getMetaData";
import { getServiceSchema } from "@/lib/serviceSchema";
import { pushCleverTapEvent } from "@/component/shared/pushCleverTapEvent";
import PartnerFormMexico from "@/component/shared/partner-program-Form/partnerFormMexico";

const Partnerprogram = () => {
  const router = useRouter();
  const { locale } = router;
  const t = (locale === 'es-mx' && esMX) || (locale === 'en-us' && enUS) || (locale === 'en-in' && enIN);
  const [mWidth, setMWidth] = useState(0);
  const [content, setContent] = useState(t.PartnershipData);
  const [activeId, setActiveId] = useState(1);
  const [autoChange, setAutoChange] = useState(true);
  const intervalRef = useRef(null); 
  const metadata = getMetadata(t);
  
  const handleWindowResize = () => {
    setMWidth(window.innerWidth);
  };

  useEffect(() => {
    AOS.init();
    handleWindowResize();
    window.addEventListener("load", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  const updateActiveContent = (id) => {
    const updatedContent = content.map((item) => ({
      ...item,
      isActive: item.id === id,
    }));
    setContent(updatedContent);
    setActiveId(id);
  };

  const handleClick = (id) => {
    setAutoChange(false); 
    updateActiveContent(id);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    setTimeout(() => {
      setAutoChange(true);
    }, 4000);
  };

  useEffect(() => {
    if (autoChange) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      intervalRef.current = setInterval(() => {
        const nextId = activeId === content.length ? 1 : activeId + 1;
        updateActiveContent(nextId);
      }, 4000);
    }
    return () => clearInterval(intervalRef.current);
  }, [activeId, autoChange, content]);
  useEffect(() => {
    setContent(t.PartnershipData);
    setActiveId(1);
    if( locale === 'en-in') {
     pushCleverTapEvent("Partner_Page_Visit", {
            "PageName": 'Partnership_Page',
          });}
  }, [t.PartnershipData, locale]);

  const renderPartnerProgramForm = () => {
    if (mWidth < 767) {
      if (locale === 'en-us') {
        return <PartnerProgramFormES />;
      } else if (locale === 'es-mx') {
        return <PartnerFormMexico />;
      } else {
        return <PartnerProgramForm />;
      }
    }
  };

  const getPartnerProgramForm = () => {
    if (locale === 'en-us') {
      return <PartnerProgramFormES />;
    } else if (locale === 'es-mx') {
      return <PartnerFormMexico />;
    } else {
      return <PartnerProgramForm />;
    }
  };
  
  const schemaData = getServiceSchema({
      metaTitle:t.SeoContent.title,
      pageUrl: t.SeoContent.url,
      websiteLogo:IMAGES.SHARED.credlixLogo.src,
      mainImageUrl:IMAGES.partnerProgram?.partnerbgDesktop?.src,
      metaDescription:t.SeoContent.description,
      pageName: t.SeoContent.title
  });

  return (
    <main>
      <Head>
              <title>{metadata.title}</title>
              <meta name="description" content={metadata.description} />
              <meta name="keywords" content={metadata.keywords} />
              <link rel="icon" href={metadata.icon} />
              <meta property="og:title" content={metadata.openGraph.title} />
              <meta property="og:description" content={metadata.openGraph.description} />
              <meta property="og:image" content={metadata.openGraph.images} />
              <meta property="og:url" content={metadata.openGraph.url} />
              <link rel="canonical" href={metadata.alternates.canonical} />
              <link rel="preload" as="image" href={IMAGES.partnerProgram?.partnerbgDesktop?.src} />
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
          />
          </Head>
      <section>
        <div
          style={{
            backgroundImage: `url(${IMAGES.partnerProgram?.partnerbgDesktop?.src})`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
          }}
          className={locale === 'en-in' ? "mt-[100px] sm:mt-28" : "mt-[75px] sm:mt-12" }>
          <div className="flex w-full justify-between container px-3 lg:px-3 xm:px-2">
            <div className="w-1/2 sm:w-full">
              <Image
                src={t.banner.imagePath}
                alt="partner logo"
                width={450}
                height={142}
                className="mt-28 sm:mt-8"
              />
              <h1 className="text-[25px] sm:text-lg text-white font-normal mt-10 sm:my-4 w-[70%] sm:w-full">
                {t.banner.bannerHeading}
              </h1>
            </div>
            {mWidth > 767 && (
              <>
                <div className="w-1/2">
                {getPartnerProgramForm()}
                </div>
              </>
            )}
          </div>
        </div>
         {renderPartnerProgramForm()}
        <div className="container px-3 lg:px-3 xm:px-2">
          {/* Partner Benefits */}
          <div className="w-full flex sm:flex-col lg:flex-row my-24 sm:my-16 gap-x-14">
            <div className="flex items-start w-5/12 sm:w-full">
              <Image
                src={t.partnerBenefitsText.imagePath}
                alt="Warehouse Image"
                width={500}
                height={579}
                quality={100}
                className="rounded-lg w-auto h-[90%] sm:w-full sm:h-auto"
              />
            </div>
            <div className="flex flex-col w-7/12 sm:w-full sm:my-5">
              <h2 className="text-3xl font-semibold mb-3">
                {t.partnerBenefitsText.subheadline}
              </h2>
              <p className="text-base mb-14 sm:mb-6 text-gray-600">
                {t.partnerBenefitsText.description}
              </p>
              <h3 className="text-2xl font-bold text-gray-800 mb-12" dangerouslySetInnerHTML={{ __html:(t.partnerBenefitsText.partnerbg) }}>
              </h3>

              <div className="flex flex-wrap gap-y-12">
                {t.partnerBenefits.map((benefit, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-3 sm:space-x-1.5 w-1/2"
                  >
                    <Image
                      src={benefit.icon}
                      alt={`${benefit.title} icon`}
                      width={58}
                      height={58}
                      className="sm:w-6 sm:h-6"
                    />
                    <div>
                      <h4 className="text-base font-bold text-gray-800 mb-2">
                        {benefit.title}
                      </h4>
                      <p className="text-base font-normal text-black w-9/12	sm:w-full">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* {Partnership Journey​ Start} */}
        <div className="bg-[#F1F0F6] mb-24 sm:my-12">
          <div className="container sm:px-3 pt-20 pb-28 sm:py-16">
            <div className="text-3xl font-bold text-center">
              {t.Partnership}
            </div>
            <div className="flex justify-between items-start gap-x-4 sm:flex-col px-5 sm:px-0 pt-16">
              <div className="space-y-6 w-1/2 sm:w-full">
                {content.map((item) => (
                  <div
                  key={item.id}
                  onClick={() => handleClick(item.id)}
                  className={`cursor-pointer pl-3 transition-all duration-300 
                  ${
                    item.isActive
                      ? "border-l-4 border-yellow-500"
                      : "border-l-4 border-transparent"
                  }`}
                >
  
                    <h2 className="text-lg font-bold text-secondary mb-1">
                      {item.title}
                    </h2>
                    <div className="text-base text-secondary">
                      <span className="font-medium mb-1">
                        {item.descriptionHeading}{" "}
                      </span>
                      <span className="font-normal">{item.description}</span>
                    </div>
                    <div className="text-base text-secondary">
                      <span className="font-medium mb-1">
                        {item.descriptionHeading2}{" "}
                      </span>
                      <span className="font-normal">{item.description2}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="ml-10 sm:mx-4 sm:mt-6">
                {content.map(
                  (item) =>
                    item.isActive && (
                      <Image
                        width={600}
                        height={419}
                        key={item.id}
                        src={item.image}
                        alt={item.title}
                        quality={100}
                        className="h-auto max-h-[400px] max-w-[450px] w-auto sm:w-full"
                      />
                    )
                )}
              </div>
            </div>
          </div>
        </div>
        {/* {Partnership Journey​ End} */}

        <div className="container px-3 lg:px-3 xm:px-2">
          {/* {Export factoring Start } */}
          <div className="text-center sm:mb-16">
            <div className="text-3xl text-secondary font-bold mb-2">
              {t.boostEarnings.exportHeading}
            </div>
           <div className="text-3xl text-secondary sm:py-2" dangerouslySetInnerHTML={{ __html:(t.boostEarnings.exportSubHeading) }}>
         </div>

            <div className={`${t.forExporterMenuItems.length == 2?'w-[900px]':'w-[1080px]'} flex items-center flex-wrap justify-between sm:flex-col sm:flex-nowrap sm:w-full m-auto mt-16`}>
              {t.forExporterMenuItems.map((item) => (
                <div
                  key={item.name}
                  className={`${locale != 'en-in'  ? 'w-[48%]' : 'w-[32%]'} h-[309px] mb-3 sm:w-full shadow-sm rounded-2xl p-2 flex items-center text-center`}
                >
                  <Link href={item.href} rel="noopener noreferrer" onClick={()=>pushCleverTapEvent(item.eventsName,{"PageName": 'Partnership_Page',})}>
                    <div className="flex flex-col items-center">
                      <Image
                        src={item.src}
                        width={50}
                        height={48}
                        alt={item.name}
                        // className="w-auto"
                      />
                      <div className="pl-2">
                        <p className="text-lg font-bold text-secondary mt-5 mb-2">
                          {item.name}
                        </p>
                        <p className="text-base font-normal text-secondary">
                          {item.text}
                        </p>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
          {/* {Export factoring Start } */}

          {/* {global Expansion​ start} */}
          <div className={`${locale === 'en-in' ? 'mt-20' : 'mt-14'} text-center text-3xl mb-24 sm:mb-12`} dangerouslySetInnerHTML={{ __html:(t.GlobalExpansion.heading) }}>
          </div>
          <div className="flex w-full sm:px-0 sm:flex-col">
            <div className="w-2/4 sm:w-full">
              <Image
                src={t.GlobalExpansion.imagePath}
                width={400}
                height={307}
                alt="Global Graphic"
                className="sm:w-full"
              />
            </div>
            <div className="w-2/4 sm:mt-6 sm:w-full">
              {t.mainContent.sections.map((section, index) => (
                <div key={index} className="mb-4">
                  <h3 className="font-bold text-xl mb-2">{section.headline}</h3>
                  <p className="text-lg font-normal">{section.description}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="flex w-full justify-evenly sm:justify-between my-24 sm:my-12 sm:gap-x-1">
            {t.statistics.map((stat, index) => (
              <div
                key={index}
                className="text-center flex flex-col items-center"
              >
                <Image
                  src={stat.icon}
                  width={68}
                  height={68}
                  alt={stat.title}
                  className="sm:w-6 sm:h-6"
                />
                <h4 className="font-bold text-3xl text-secondary mt-4 sm:mt-2 sm:text-lg sm:min-h-14">
                  {stat.title}
                </h4>
                <p className="text-xl font-normal text-secondary mt-2 sm:text-base">
                  {stat.description}
                </p>
              </div>
            ))}
          </div>

          {/* {global Expansion​ end} */}
          {t.channelPartnerLogoHeading &&
          <div className="pb-24 sm:pb-8">
          <h2 className="text-3xl sm:text-xl font-bold text-center mb-1">
           {t.channelPartnerLogoHeading}</h2>
         <div className="flex sm:flex-wrap w-4/5 mx-auto gap-4 py-4">
          {t.channelPartnerLogo.map((feature) => (
            <div
              key={feature.id}
              className="text-center mx-auto">
              <div className="flex justify-center">
                <Image
                  src={feature.image}
                  alt={feature.alt}
                  width={255}
                  height={100}
                  quality={true}
                  className="w-auto h-auto object-contain"
                />
              </div>
            </div>
          ))}
          </div>
          </div>}
        </div>
      </section>
    </main>
  );
};

export default Partnerprogram;
